import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../utils/api';
import {
  Smartphone,
  Users,
  CreditCard,
  Wifi,
  MapPin,
  ShieldCheck,
  Search,
  Filter,
  Eye,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import PaginationControls from '../../components/admin/PaginationControls';

const StatCard = ({ title, value, icon: Icon, meta }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
        <p className="text-xs text-gray-500 mt-1">{meta}</p>
      </div>
      <div className="p-2 bg-gray-100 rounded-lg">
        <Icon className="h-5 w-5 text-gray-600" />
      </div>
    </div>
  </div>
);

const TableCard = ({
  title,
  subtitle,
  columns,
  rows,
  emptyText,
  controls,
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  itemLabel,
}) => (
  <div className="bg-white rounded-lg border border-gray-200">
    <div className="border-b border-gray-200 px-6 py-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {subtitle ? <p className="mt-1 text-sm text-gray-600">{subtitle}</p> : null}
        </div>
        {controls ? <div className="w-full max-w-3xl">{controls}</div> : null}
      </div>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th key={column.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500">
                {emptyText}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={row.id || index}>
                {columns.map((column) => (
                  <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {column.render ? column.render(row[column.key], row) : (row[column.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>

    <div className="px-6 py-4">
      <PaginationControls
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={[5, 10, 20]}
        itemLabel={itemLabel}
      />
    </div>
  </div>
);

const MobilePlatform = () => {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [devicePlatformFilter, setDevicePlatformFilter] = useState('all');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(5);
  const [devicesPage, setDevicesPage] = useState(1);
  const [devicesPageSize, setDevicesPageSize] = useState(5);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPageSize, setOrdersPageSize] = useState(5);

  // User management (view / edit / delete) state.
  const [detail, setDetail] = useState(null); // { user, devices, orders, vouchers, vpn_profiles }
  const [detailLoading, setDetailLoading] = useState(false);
  const [editUser, setEditUser] = useState(null); // the user being edited
  const [editForm, setEditForm] = useState({ full_name: '', phone: '', email: '', preferred_language: '', status: 'active' });
  const [deleteTarget, setDeleteTarget] = useState(null); // the user pending deletion
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  const refreshUsers = async () => {
    const [usersResponse, overviewResponse] = await Promise.all([
      adminAPI.getMobileUsers(),
      adminAPI.getMobileOverview(),
    ]);
    setUsers(usersResponse.data.users || []);
    setOverview(overviewResponse.data);
  };

  const openDetail = async (user) => {
    setDetail(null);
    setDetailLoading(true);
    setActionError('');
    try {
      const response = await adminAPI.getMobileUser(user.id);
      setDetail(response.data);
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to load user detail');
      setDetailLoading(false);
      return;
    }
    setDetailLoading(false);
  };

  const openEdit = (user) => {
    setActionError('');
    setEditUser(user);
    setEditForm({
      full_name: user.full_name || '',
      phone: user.phone || '',
      email: user.email || '',
      preferred_language: user.preferred_language || 'en',
      status: user.status || 'active',
    });
  };

  const saveEdit = async () => {
    if (!editUser) return;
    setActionLoading(true);
    setActionError('');
    try {
      await adminAPI.updateMobileUser(editUser.id, editForm);
      await refreshUsers();
      setNotice(`Updated ${editForm.full_name || editForm.phone}`);
      setEditUser(null);
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    setActionError('');
    try {
      await adminAPI.deleteMobileUser(deleteTarget.id);
      await refreshUsers();
      setNotice(`Deleted ${deleteTarget.full_name || deleteTarget.phone}`);
      setDeleteTarget(null);
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      try {
        setError('');
        const [overviewResponse, usersResponse, devicesResponse, ordersResponse] = await Promise.all([
          adminAPI.getMobileOverview(),
          adminAPI.getMobileUsers(),
          adminAPI.getMobileDevices(),
          adminAPI.getMobileOrders(),
        ]);

        if (!active) {
          return;
        }

        setOverview(overviewResponse.data);
        setUsers(usersResponse.data.users || []);
        setDevices(devicesResponse.data.devices || []);
        setOrders(ordersResponse.data.orders || []);
      } catch (loadError) {
        console.error('Failed to load mobile platform data:', loadError);
        if (active) {
          setError(loadError.response?.data?.error || 'Failed to load mobile platform data');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setUsersPage(1);
    setDevicesPage(1);
    setOrdersPage(1);
  }, [searchTerm, devicePlatformFilter, orderStatusFilter, usersPageSize, devicesPageSize, ordersPageSize]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredUsers = useMemo(() => {
    return users.filter((user) => (
      !normalizedSearch || [
        user.full_name,
        user.phone,
        user.email,
      ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch))
    ));
  }, [users, normalizedSearch]);

  const filteredDevices = useMemo(() => {
    return devices.filter((device) => {
      const matchesSearch = !normalizedSearch || [
        device.full_name,
        device.platform,
        device.os_version,
        device.model,
        device.wireguard_ip_cidr,
      ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));

      const platform = String(device.platform || '').toLowerCase() || 'unknown';
      const matchesPlatform = devicePlatformFilter === 'all' || platform === devicePlatformFilter;

      return matchesSearch && matchesPlatform;
    });
  }, [devices, normalizedSearch, devicePlatformFilter]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchesSearch = !normalizedSearch || [
        order.full_name,
        order.package_name,
        order.voucher_code,
        order.currency,
        order.status,
      ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));

      const matchesStatus = orderStatusFilter === 'all' || String(order.status || '').toLowerCase() === orderStatusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [orders, normalizedSearch, orderStatusFilter]);

  const paginatedUsers = useMemo(() => {
    const start = (usersPage - 1) * usersPageSize;
    return filteredUsers.slice(start, start + usersPageSize);
  }, [filteredUsers, usersPage, usersPageSize]);

  const paginatedDevices = useMemo(() => {
    const start = (devicesPage - 1) * devicesPageSize;
    return filteredDevices.slice(start, start + devicesPageSize);
  }, [filteredDevices, devicesPage, devicesPageSize]);

  const paginatedOrders = useMemo(() => {
    const start = (ordersPage - 1) * ordersPageSize;
    return filteredOrders.slice(start, start + ordersPageSize);
  }, [filteredOrders, ordersPage, ordersPageSize]);

  const devicePlatformOptions = useMemo(() => {
    const values = Array.from(new Set(
      devices
        .map((device) => String(device.platform || '').trim().toLowerCase())
        .filter(Boolean)
    ));
    return values.sort();
  }, [devices]);

  const orderStatusOptions = useMemo(() => {
    const values = Array.from(new Set(
      orders
        .map((order) => String(order.status || '').trim().toLowerCase())
        .filter(Boolean)
    ));
    return values.sort();
  }, [orders]);

  const formatDateTime = (value) => {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  return (
    <div className="p-8">
      <div className="bg-white border-b border-gray-200 px-8 py-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Smartphone className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Mobile Platform</h1>
            <p className="text-sm text-gray-600 mt-1">
              Review app registrations, mobile devices, purchases, and VPN provisioning in one responsive view.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_16rem_16rem]">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search users, devices, orders, voucher codes"
              className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <select
              value={devicePlatformFilter}
              onChange={(event) => setDevicePlatformFilter(event.target.value)}
              className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Platforms</option>
              {devicePlatformOptions.map((platform) => (
                <option key={platform} value={platform}>
                  {platform}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Filter className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <select
              value={orderStatusFilter}
              onChange={(event) => setOrderStatusFilter(event.target.value)}
              className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Order Statuses</option>
              {orderStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-3xl border border-[#C84F5D]/20 bg-[#C84F5D]/6 px-5 py-4 text-sm text-[#C84F5D]">
          {error}
        </div>
      ) : null}

      {notice ? (
        <div className="mb-6 flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-5 py-3 text-sm text-green-700">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice('')} className="text-green-700 hover:text-green-900">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5 mb-6">
        <StatCard title="Users" value={loading ? '...' : overview?.total_users ?? 0} icon={Users} meta="Registered accounts" />
        <StatCard title="Devices" value={loading ? '...' : overview?.total_devices ?? 0} icon={Smartphone} meta="Linked handsets" />
        <StatCard title="Orders" value={loading ? '...' : overview?.total_orders ?? 0} icon={CreditCard} meta="Created purchases" />
        <StatCard title="Paid Orders" value={loading ? '...' : overview?.paid_orders ?? 0} icon={ShieldCheck} meta="Captured successfully" />
        <StatCard title="VPN Profiles" value={loading ? '...' : overview?.provisioned_vpn_profiles ?? 0} icon={Wifi} meta="Provisioned configs" />
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-600 mt-4">Loading mobile platform data...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <TableCard
            title="Registered Users"
            subtitle="Mobile app accounts, filtered by the shared search box."
            rows={paginatedUsers}
            totalItems={filteredUsers.length}
            page={usersPage}
            pageSize={usersPageSize}
            onPageChange={setUsersPage}
            onPageSizeChange={setUsersPageSize}
            itemLabel="users"
            emptyText="No mobile users match the current filters."
            columns={[
              { key: 'full_name', label: 'Name', render: (value) => <span className="break-words font-medium text-gray-900">{value || '—'}</span> },
              { key: 'phone', label: 'Phone', render: (value) => <span className="break-all">{value || '—'}</span> },
              { key: 'email', label: 'Email', render: (value) => <span className="break-all">{value || '—'}</span> },
              { key: 'device_count', label: 'Devices' },
              { key: 'created_at', label: 'Registered', render: (value) => formatDateTime(value) },
              {
                key: 'actions',
                label: 'Actions',
                render: (value, row) => (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openDetail(row)}
                      title="View details"
                      className="inline-flex items-center justify-center rounded-md border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(row)}
                      title="Edit user"
                      className="inline-flex items-center justify-center rounded-md border border-gray-200 p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => { setActionError(''); setDeleteTarget(row); }}
                      title="Delete user"
                      className="inline-flex items-center justify-center rounded-md border border-gray-200 p-2 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ),
              },
            ]}
          />

          <TableCard
            title="Devices"
            subtitle="Platform filter applies here, along with the shared search term."
            rows={paginatedDevices}
            totalItems={filteredDevices.length}
            page={devicesPage}
            pageSize={devicesPageSize}
            onPageChange={setDevicesPage}
            onPageSizeChange={setDevicesPageSize}
            itemLabel="devices"
            emptyText="No devices match the current filters."
            columns={[
              { key: 'full_name', label: 'Owner', render: (value) => <span className="break-words font-medium text-gray-900">{value || '—'}</span> },
              { key: 'platform', label: 'Platform', render: (value, row) => `${value || '—'} ${row.os_version || ''}`.trim() || '—' },
              { key: 'model', label: 'Model', render: (value) => <span className="break-words">{value || '—'}</span> },
              { key: 'wireguard_ip_cidr', label: 'VPN IP', render: (value) => <span className="break-all">{value || '—'}</span> },
              {
                key: 'location_lat',
                label: 'Location',
                render: (value, row) =>
                  value && row.location_lng ? (
                    <a
                      href={`https://www.google.com/maps?q=${value},${row.location_lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex min-w-[10rem] items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      title="View on Google Maps"
                    >
                      <MapPin className="h-4 w-4 shrink-0" />
                      <span className="break-all">
                        {Number(value).toFixed(4)}, {Number(row.location_lng).toFixed(4)}
                      </span>
                    </a>
                  ) : (
                    '—'
                  ),
              },
              { key: 'created_at', label: 'Added', render: (value) => formatDateTime(value) },
            ]}
          />

          <TableCard
            title="Orders"
            subtitle="Order status filter applies here, along with the shared search term."
            rows={paginatedOrders}
            totalItems={filteredOrders.length}
            page={ordersPage}
            pageSize={ordersPageSize}
            onPageChange={setOrdersPage}
            onPageSizeChange={setOrdersPageSize}
            itemLabel="orders"
            emptyText="No mobile purchases match the current filters."
            columns={[
              { key: 'full_name', label: 'User', render: (value) => <span className="break-words font-medium text-gray-900">{value || '—'}</span> },
              { key: 'package_name', label: 'Package', render: (value) => <span className="break-words">{value || '—'}</span> },
              { key: 'amount', label: 'Amount', render: (value, row) => `${row.currency} ${Number(value || 0).toFixed(2)}` },
              { key: 'status', label: 'Status' },
              { key: 'voucher_code', label: 'Voucher', render: (value) => <span className="break-all">{value || '—'}</span> },
              { key: 'created_at', label: 'Created', render: (value) => formatDateTime(value) },
            ]}
          />
        </div>
      )}

      {/* View detail modal */}
      {(detailLoading || detail) ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => { setDetail(null); setDetailLoading(false); }}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">User Details</h3>
              <button type="button" onClick={() => { setDetail(null); setDetailLoading(false); }} className="text-gray-400 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              {detailLoading ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
                </div>
              ) : detail ? (
                <div className="space-y-6 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div><p className="text-gray-500">Name</p><p className="font-medium text-gray-900">{detail.user.full_name || '—'}</p></div>
                    <div><p className="text-gray-500">Status</p><p className="font-medium text-gray-900">{detail.user.status || '—'}</p></div>
                    <div><p className="text-gray-500">Phone</p><p className="font-medium text-gray-900 break-all">{detail.user.phone || '—'}</p></div>
                    <div><p className="text-gray-500">Email</p><p className="font-medium text-gray-900 break-all">{detail.user.email || '—'}</p></div>
                    <div><p className="text-gray-500">Language</p><p className="font-medium text-gray-900">{detail.user.preferred_language || '—'}</p></div>
                    <div><p className="text-gray-500">Registered</p><p className="font-medium text-gray-900">{formatDateTime(detail.user.created_at)}</p></div>
                  </div>

                  <div>
                    <p className="mb-2 font-semibold text-gray-900">Devices ({detail.devices.length})</p>
                    {detail.devices.length === 0 ? <p className="text-gray-500">No devices.</p> : (
                      <ul className="space-y-1">
                        {detail.devices.map((d) => (
                          <li key={d.id} className="rounded-md bg-gray-50 px-3 py-2">
                            <span className="font-medium text-gray-900">{d.model || 'Device'}</span>
                            <span className="text-gray-500"> · {d.platform} {d.os_version} · {d.wireguard_ip_cidr || 'no VPN IP'}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <p className="mb-2 font-semibold text-gray-900">Orders ({detail.orders.length})</p>
                    {detail.orders.length === 0 ? <p className="text-gray-500">No orders.</p> : (
                      <ul className="space-y-1">
                        {detail.orders.map((o) => (
                          <li key={o.id} className="rounded-md bg-gray-50 px-3 py-2">
                            <span className="font-medium text-gray-900">{o.package_name || 'Package'}</span>
                            <span className="text-gray-500"> · {o.currency} {Number(o.amount || 0).toFixed(2)} · {o.status}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <p className="mb-2 font-semibold text-gray-900">Vouchers ({detail.vouchers.length})</p>
                    {detail.vouchers.length === 0 ? <p className="text-gray-500">No vouchers.</p> : (
                      <ul className="space-y-1">
                        {detail.vouchers.map((v) => (
                          <li key={v.id} className="rounded-md bg-gray-50 px-3 py-2">
                            <span className="font-medium text-gray-900 break-all">{v.voucher_code}</span>
                            <span className="text-gray-500"> · {v.status}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* Edit modal */}
      {editUser ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !actionLoading && setEditUser(null)}>
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit User</h3>
              <button type="button" onClick={() => setEditUser(null)} className="text-gray-400 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-6 py-5">
              {actionError ? <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</p> : null}
              {[
                { key: 'full_name', label: 'Full name', type: 'text' },
                { key: 'phone', label: 'Phone', type: 'text' },
                { key: 'email', label: 'Email', type: 'email' },
                { key: 'preferred_language', label: 'Language', type: 'text' },
              ].map((field) => (
                <div key={field.key}>
                  <label className="mb-1 block text-sm font-medium text-gray-700">{field.label}</label>
                  <input
                    type={field.type}
                    value={editForm[field.key]}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">active</option>
                  <option value="suspended">suspended</option>
                  <option value="disabled">disabled</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button type="button" onClick={() => setEditUser(null)} disabled={actionLoading} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button type="button" onClick={saveEdit} disabled={actionLoading} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">{actionLoading ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Delete confirmation */}
      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !actionLoading && setDeleteTarget(null)}>
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5">
              <h3 className="text-lg font-semibold text-gray-900">Delete user?</h3>
              <p className="mt-2 text-sm text-gray-600">
                This permanently removes <span className="font-medium">{deleteTarget.full_name || deleteTarget.phone}</span> and all of their devices, orders, vouchers and VPN profiles. This cannot be undone.
              </p>
              {actionError ? <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</p> : null}
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={actionLoading} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button type="button" onClick={confirmDelete} disabled={actionLoading} className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">{actionLoading ? 'Deleting…' : 'Delete user'}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MobilePlatform;
